import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SHOW_SOFTWARE } from '../siteConfig';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Prevent body scroll when menu is open, and close it with Escape
  useEffect(() => {
    if (!menuOpen) return undefined;

    document.body.style.overflow = 'hidden';
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [menuOpen]);

  const navLinks = [
    { name: 'ABOUT', href: '#about' },
    { name: 'WORKS', href: '#works' },
    { name: 'SERVICES', href: '#services' },
    { name: 'STUDIO', href: '#studio' },
    { name: 'ORIGINAL PROJECTS', href: '#inhouse' },
    ...(SHOW_SOFTWARE ? [{ name: 'SOFTWARE', href: '#software' }] : []),
    { name: 'CLIENTS', href: '#clients' },
    { name: 'CONTACT', href: '#contact' },
  ];

  const menuVariants = {
    closed: {
      opacity: 0,
      transition: {
        duration: 0.5,
        ease: [0.22, 1, 0.36, 1],
      },
    },
    open: {
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  const linkContainerVariants = {
    closed: {
      transition: {
        staggerChildren: 0.05,
        staggerDirection: -1,
      },
    },
    open: {
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.2,
      },
    },
  };

  const linkVariants = {
    closed: {
      y: 50,
      opacity: 0,
      transition: {
        duration: 0.4,
        ease: [0.22, 1, 0.36, 1],
      },
    },
    open: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  return (
    <>
      {/* Fixed Navbar */}
      <motion.nav
        data-testid="navbar"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled && !menuOpen ? 'glass' : 'bg-transparent'
        }`}
      >
        <div className="px-6 md:px-12 lg:px-24 py-4 md:py-5 flex items-center justify-between">
          {/* Logo */}
          <a
            href="#"
            className="z-50 relative hover:opacity-70 transition-opacity"
            data-testid="logo-link"
            onClick={() => menuOpen && setMenuOpen(false)}
          >
            <img
              src="/assets/audioxl-logo.png"
              alt="AudioXL"
              className="h-10 md:h-14 w-auto"
            />
          </a>

          {/* Menu Toggle Button - Hamburger Icon */}
          <button
            type="button"
            onClick={() => setMenuOpen(!menuOpen)}
            className="relative z-50 w-11 h-11 flex flex-col items-center justify-center gap-[6px] group"
            data-testid="menu-toggle"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            aria-controls="site-menu"
          >
            <motion.span
              animate={menuOpen ? { rotate: 45, y: 8 } : { rotate: 0, y: 0 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="w-8 h-[2px] bg-white block origin-center"
            />
            <motion.span
              animate={menuOpen ? { opacity: 0, scaleX: 0 } : { opacity: 1, scaleX: 1 }}
              transition={{ duration: 0.2 }}
              className="w-8 h-[2px] bg-white block"
            />
            <motion.span
              animate={menuOpen ? { rotate: -45, y: -8 } : { rotate: 0, y: 0 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="w-8 h-[2px] bg-white block origin-center"
            />
          </button>
        </div>
      </motion.nav>

      {/* Fullscreen Menu Overlay */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            id="site-menu"
            variants={menuVariants}
            initial="closed"
            animate="open"
            exit="closed"
            className="fixed inset-0 z-40 bg-[#050505] flex items-center justify-center"
            data-testid="fullscreen-menu"
          >
            {/* Menu Content */}
            <motion.nav
              aria-label="Main"
              variants={linkContainerVariants}
              initial="closed"
              animate="open"
              exit="closed"
              className="relative z-10 flex flex-col items-center gap-3 md:gap-4"
            >
              {navLinks.map((link) => (
                <motion.div
                  key={link.name}
                  variants={linkVariants}
                  className="overflow-hidden"
                >
                  <a
                    href={link.href}
                    onClick={() => setMenuOpen(false)}
                    className="font-heading font-bold text-xl sm:text-2xl md:text-3xl text-white hover:text-white/50 transition-colors duration-300 block relative group tracking-wide"
                    data-testid={`menu-link-${link.name.toLowerCase()}`}
                  >
                    <span className="relative inline-block">
                      {link.name}
                      <span className="absolute left-0 bottom-0 w-0 h-[1px] bg-white group-hover:w-full transition-all duration-500 ease-out" />
                    </span>
                  </a>
                </motion.div>
              ))}
            </motion.nav>

            {/* Bottom Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ delay: 0.5, duration: 0.5 }}
              className="absolute bottom-8 left-0 right-0 px-6 md:px-12 lg:px-24"
            >
              <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-white/60">
                <span className="font-body text-xs tracking-[0.2em] uppercase">
                  Audio Specialist and Music Consulting
                </span>
                <span className="font-body text-xs tracking-[0.2em] uppercase">
                  Milan, Italy
                </span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
